/**
 * Reports List Route (Story 5.3)
 *
 * Displays the reports list page. Shows a table view (default) or card grid as an
 * alternative via the view switcher when reports exist, or a polished empty state
 * with feature hints when no reports exist. Filtering, sort, and view preferences
 * are persisted via localStorage.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Files,
  ChartBar,
  PaintBrush,
  FilePdf,
  Trash,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@provenleap/ui";
import { apiGet, apiDelete, apiPost } from "../../../lib/apiClient";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { ReportTableView } from "../../../components/reports/ReportTableView";
import type { ReportSortOption } from "../../../components/reports/ReportTableView";
import { ReportCardGrid } from "../../../components/reports/ReportCardGrid";
import { SubmitForReviewDialog } from "../../../components/reports/SubmitForReviewDialog";
import { AssignReviewerDialog } from "../../../components/reports/AssignReviewerDialog";
import {
  ReviewActionDialog,
  type ReviewAction,
} from "../../../components/reports/ReviewActionDialog";
import { ApprovalHistoryDialog } from "../../../components/reports/ApprovalHistoryDialog";
import { useToast } from "../../../hooks/useToast";
import { Toast } from "../../../components/clients/Toast";
import { ReportFilterToolbar } from "../../../components/reports/ReportFilterToolbar";
import { REPORT_HIDEABLE_COLUMNS } from "../../../components/reports/reportColumns";
import { useHiddenColumns } from "../../../components/shared/columnVisibility";
import { PageSpinner } from "../../../components/PageSpinner";

// ============================================================================
// Types
// ============================================================================

interface ReportListItem {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  status: "draft" | "ready";
  format: "document" | "slide-deck";
  dateRangeStart: string;
  dateRangeEnd: string;
  pdfUrl: string | null;
  createdAt: number;
  updatedAt: number;
  // Story 24.2 — approval state (orthogonal to status). null = never submitted.
  approvalStatus?:
    | "pending_review"
    | "approved"
    | "changes_requested"
    | "withdrawn"
    | null;
  // Story 24.3 — assigned reviewer.
  reviewerId?: string | null;
  reviewerName?: string | null;
  reviewerEmail?: string | null;
}

interface ClientOption {
  id: string;
  name: string;
}

type ReportStatus = ReportListItem["status"];
type ReportView = "table" | "grid";

// Story 24.4 — per-verdict success-toast copy (module scope; not re-allocated).
const REVIEW_TOAST: Record<ReviewAction, string> = {
  approve: "Report approved",
  request_changes: "Changes requested",
  withdraw: "Submission withdrawn",
};

// ============================================================================
// Route
// ============================================================================

export const Route = createFileRoute("/_authenticated/reports/")({
  component: ReportsPage,
});

function ReportsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const canManageReports = hasPermission(Actions.MANAGE_REPORTS);
  const [isExiting, setIsExiting] = useState(false);
  const [pendingNav, setPendingNav] = useState<string | null>(null);

  // ── View + filter state (persisted) ────────────────────────────────
  const [view, setView] = useState<ReportView>(() => {
    if (typeof window === "undefined") return "table";
    return (localStorage.getItem("reports-view") as ReportView) ?? "table";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<ReportStatus[]>([]);
  const [sortBy, setSortBy] = useState<ReportSortOption>("created_desc");
  const [deleteTarget, setDeleteTarget] = useState<ReportListItem | null>(null);
  const [submitTarget, setSubmitTarget] = useState<ReportListItem | null>(null);
  const [assignTarget, setAssignTarget] = useState<ReportListItem | null>(null);
  const [reviewTarget, setReviewTarget] = useState<{
    report: ReportListItem;
    action: ReviewAction;
  } | null>(null);
  const [historyTarget, setHistoryTarget] = useState<ReportListItem | null>(null);
  const { toast, showToast } = useToast();

  // Hidden table columns — per-device, persisted to localStorage. Shared by the
  // table (hide buttons) and the toolbar's Columns menu (restore).
  const { hiddenColumns, toggleColumn } = useHiddenColumns(
    "reportTable_hiddenColumns",
    REPORT_HIDEABLE_COLUMNS,
  );

  // Action menu trigger refs (focus restoration on dropdown close)
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("reports-view", view);
  }, [view]);

  useEffect(() => {
    if (!pendingNav) return;
    const timer = setTimeout(() => {
      navigate({ to: pendingNav });
    }, 250);
    return () => clearTimeout(timer);
  }, [pendingNav, navigate]);

  const handleNewReport = () => {
    setIsExiting(true);
    setPendingNav("/reports/generate");
  };

  const {
    data: reports,
    isLoading,
    error,
  } = useQuery<ReportListItem[]>({
    queryKey: ["reports"],
    queryFn: async () => {
      const res = await apiGet("/api/reports");
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? "Failed to load reports");
      }
      const json = await res.json();
      return json.data;
    },
  });

  const { data: hasClients } = useQuery<boolean>({
    queryKey: ["clients-exist"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) return false;
      const json = await res.json();
      return Array.isArray(json.data) && json.data.length > 0;
    },
  });

  // Clients list used to power the "Filter by client" dropdown in the toolbar.
  const { data: clients } = useQuery<ClientOption[]>({
    queryKey: ["clients-list-for-reports-filter"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) return [];
      const json = await res.json();
      return json.data ?? [];
    },
  });

  const isEmpty = !isLoading && !error && reports && reports.length === 0;
  const hasReports = !isLoading && !error && reports && reports.length > 0;

  // ── Filtering + sorting pipeline ───────────────────────────────────
  const filteredAndSortedReports = useMemo(() => {
    if (!reports) return [] as ReportListItem[];

    const q = searchQuery.trim().toLowerCase();
    let result = q
      ? reports.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            r.clientName.toLowerCase().includes(q)
        )
      : reports.slice();

    if (selectedClientIds.length > 0) {
      result = result.filter((r) => selectedClientIds.includes(r.clientId));
    }

    if (selectedStatuses.length > 0) {
      result = result.filter((r) => selectedStatuses.includes(r.status));
    }

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "client_asc":
          return a.clientName.localeCompare(b.clientName);
        case "client_desc":
          return b.clientName.localeCompare(a.clientName);
        case "status_asc":
          return a.status.localeCompare(b.status);
        case "status_desc":
          return b.status.localeCompare(a.status);
        case "format_asc":
          return a.format.localeCompare(b.format);
        case "format_desc":
          return b.format.localeCompare(a.format);
        case "date_range_asc":
          return a.dateRangeStart.localeCompare(b.dateRangeStart);
        case "date_range_desc":
          return b.dateRangeStart.localeCompare(a.dateRangeStart);
        case "created_asc":
          return a.createdAt - b.createdAt;
        case "created_desc":
          return b.createdAt - a.createdAt;
        case "updated_asc":
          return a.updatedAt - b.updatedAt;
        case "updated_desc":
          return b.updatedAt - a.updatedAt;
        default:
          return 0;
      }
    });

    return result;
  }, [
    reports,
    searchQuery,
    selectedClientIds,
    selectedStatuses,
    sortBy,
  ]);

  // ── Row action handlers ────────────────────────────────────────────
  const handleDuplicate = useCallback((_report: ReportListItem) => {
    // TODO: Wire to POST /api/reports once duplicate endpoint exists.
    if (typeof window !== "undefined") {
      // Lightweight feedback until full duplicate flow lands.
      window.alert("Duplicate is not yet implemented.");
    }
  }, []);

  const handleDownload = useCallback(
    async (report: ReportListItem) => {
      // (Re)generate so the download reflects the latest saved data, then stream
      // the PDF via the authenticated endpoint as a blob → browser download.
      // (The old code did window.open(report.pdfUrl) on a raw R2 key, which the
      // SPA router swallowed as a route → "Not Found".)
      showToast("info", `Generating PDF for ${report.name}…`);
      try {
        const gen = await apiPost(`/api/reports/${report.id}/generate-pdf`);
        if (!gen.ok) {
          const body = (await gen.json().catch(() => null)) as
            | { detail?: string }
            | null;
          throw new Error(body?.detail ?? "Could not generate the PDF");
        }
        const res = await apiGet(`/api/reports/${report.id}/pdf`);
        if (!res.ok) throw new Error("Could not download the PDF");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const cd = res.headers.get("Content-Disposition");
        const filename =
          cd?.match(/filename="(.+?)"/)?.[1] ?? `${report.name}.pdf`;
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        showToast("success", "PDF downloaded");
      } catch (e) {
        showToast(
          "error",
          e instanceof Error ? e.message : "Could not download the PDF",
        );
      }
    },
    [showToast],
  );

  // Delete flow — proprietary §4.4 confirmation modal (replaces the old native
  // window.confirm) wired to the tenant-scoped DELETE /api/reports/:id endpoint.
  const deleteMutation = useMutation({
    mutationFn: async (report: ReportListItem) => {
      const res = await apiDelete(`/api/reports/${report.id}`);
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ detail: "Delete failed" }));
        throw new Error(err.detail ?? "Delete failed");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
      setDeleteTarget(null);
    },
  });

  // Row action just opens the confirmation modal; reset clears any prior error.
  const handleDelete = useCallback(
    (report: ReportListItem) => {
      deleteMutation.reset();
      setDeleteTarget(report);
    },
    [deleteMutation]
  );

  // Submit-for-review flow (Story 24.2) — POST /api/reports/:id/submit.
  const submitMutation = useMutation({
    mutationFn: async (report: ReportListItem) => {
      const res = await apiPost(`/api/reports/${report.id}/submit`);
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ detail: "Submit failed" }));
        throw new Error(err.detail ?? err.title ?? "Submit failed");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
      setSubmitTarget(null);
      showToast("success", "Submitted for review");
    },
    // No onError toast — the dialog stays open and surfaces the error inline
    // (single channel, matching the delete flow).
  });

  const handleSubmitForReview = useCallback(
    (report: ReportListItem) => {
      submitMutation.reset();
      setSubmitTarget(report);
    },
    [submitMutation]
  );

  // Reviewer-assignment flow (Story 24.3) — the dialog owns its own fetch+PATCH.
  const handleAssignReviewer = useCallback((report: ReportListItem) => {
    setAssignTarget(report);
  }, []);

  // Verdict flow (Story 24.4) — approve / request changes / withdraw.
  const handleReviewAction = useCallback(
    (report: ReportListItem, action: ReviewAction) => {
      setReviewTarget({ report, action });
    },
    [],
  );

  // Approval history (Story 24.6) — read-only timeline dialog.
  const handleHistory = useCallback((report: ReportListItem) => {
    setHistoryTarget(report);
  }, []);

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Reports" }]} />
      <div className="mb-6" />

      {/* Filter toolbar — shown when reports exist */}
      {hasReports && (
        <ReportFilterToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          availableClients={clients ?? []}
          selectedClientIds={selectedClientIds}
          onClientFilterChange={setSelectedClientIds}
          selectedStatuses={selectedStatuses}
          onStatusFilterChange={setSelectedStatuses}
          view={view}
          onViewChange={setView}
          canManageReports={canManageReports}
          onNewReport={handleNewReport}
          newReportDisabled={hasClients === false}
          hiddenColumns={hiddenColumns}
          onToggleColumn={toggleColumn}
          showColumnsControl={view === "table"}
        />
      )}

      {/* Loading state */}
      {isLoading && <PageSpinner />}

      {/* Error state */}
      {error && (
        <div
          className="rounded-md border border-destructive bg-destructive/5 p-4 text-sm text-destructive"
          role="alert"
        >
          {error.message}
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* Reports — table or grid view */}
        {hasReports && !isExiting && (
          <motion.div
            key={`reports-${view}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {view === "table" ? (
              <ReportTableView
                reports={filteredAndSortedReports}
                canManageReports={canManageReports}
                onReportClick={(report) =>
                  navigate({
                    to: "/reports/$reportId/edit",
                    params: { reportId: report.id },
                  })
                }
                onEditClick={(report) =>
                  navigate({
                    to: "/reports/$reportId/edit",
                    params: { reportId: report.id },
                  })
                }
                onDuplicateClick={handleDuplicate}
                onDownloadClick={handleDownload}
                onDeleteClick={handleDelete}
                onSubmitClick={handleSubmitForReview}
                onAssignClick={handleAssignReviewer}
                onReviewClick={handleReviewAction}
                onHistoryClick={handleHistory}
                triggerRefs={triggerRefs}
                sortBy={sortBy}
                onSortChange={setSortBy}
                hiddenColumns={hiddenColumns}
                onHideColumn={toggleColumn}
              />
            ) : (
              <ReportCardGrid
                reports={filteredAndSortedReports}
                canManageReports={canManageReports}
                onReportClick={(report) =>
                  navigate({
                    to: "/reports/$reportId/edit",
                    params: { reportId: report.id },
                  })
                }
                onEditClick={(report) =>
                  navigate({
                    to: "/reports/$reportId/edit",
                    params: { reportId: report.id },
                  })
                }
                onDuplicateClick={handleDuplicate}
                onDownloadClick={handleDownload}
                onDeleteClick={handleDelete}
                onSubmitClick={handleSubmitForReview}
                onAssignClick={handleAssignReviewer}
                onReviewClick={handleReviewAction}
                onHistoryClick={handleHistory}
                triggerRefs={triggerRefs}
              />
            )}
          </motion.div>
        )}

        {/* Empty state */}
        {isEmpty && !isExiting && (
          <motion.div
            key="reports-empty"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <div className="max-w-2xl mx-auto pt-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold tracking-tight">
                  No reports yet
                </h2>
                <p className="text-foreground/70 mt-1">
                  Create your first report to share marketing insights with your
                  clients.
                </p>
              </div>

              <div className="flex justify-center mb-10">
                <div className="relative group">
                  <button
                    type="button"
                    disabled={hasClients === false}
                    onClick={handleNewReport}
                    className="inline-flex items-center px-8 py-2.5 rounded-2xl bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Files size={18} className="mr-2" />
                    Create Your First Report
                  </button>
                  {hasClients === false && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-1.5 bg-popover border rounded-md shadow-md text-xs text-muted-foreground whitespace-nowrap hidden group-hover:block z-10">
                      Add a client first to create reports
                    </div>
                  )}
                </div>
              </div>

              {/* Format mockups */}
              <div className="flex items-end justify-center gap-8">
                {/* A4 Document mockup */}
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="w-28 h-36 rounded-lg bg-background p-2.5 flex flex-col gap-1.5"
                    style={{
                      border: "0.5px solid var(--hairline)",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-primary/30 shrink-0" />
                      <div className="h-1.5 w-full rounded-sm bg-muted-foreground/20" />
                    </div>
                    <div className="flex gap-1">
                      <div className="flex-1 h-5 rounded bg-primary/20 flex flex-col items-center justify-center gap-0.5">
                        <div className="h-1 w-4 rounded-sm bg-primary/40" />
                        <div className="h-0.5 w-3 rounded-sm bg-primary/25" />
                      </div>
                      <div className="flex-1 h-5 rounded bg-primary/15 flex flex-col items-center justify-center gap-0.5">
                        <div className="h-1 w-4 rounded-sm bg-primary/35" />
                        <div className="h-0.5 w-3 rounded-sm bg-primary/20" />
                      </div>
                      <div className="flex-1 h-5 rounded bg-primary/10 flex flex-col items-center justify-center gap-0.5">
                        <div className="h-1 w-4 rounded-sm bg-primary/30" />
                        <div className="h-0.5 w-3 rounded-sm bg-primary/15" />
                      </div>
                    </div>
                    <div className="flex-1 flex items-end gap-0.5 px-1">
                      <div className="w-full h-[60%] rounded-t-sm bg-primary/25" />
                      <div className="w-full h-[85%] rounded-t-sm bg-primary/30" />
                      <div className="w-full h-[45%] rounded-t-sm bg-primary/20" />
                      <div className="w-full h-[70%] rounded-t-sm bg-primary/25" />
                      <div className="w-full h-[55%] rounded-t-sm bg-primary/20" />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <div className="h-1 w-full rounded-sm bg-muted-foreground/15" />
                      <div className="h-1 w-3/4 rounded-sm bg-muted-foreground/10" />
                    </div>
                  </div>
                </div>

                {/* Slide Deck mockup */}
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="w-40 h-[94px] rounded-lg bg-background p-2.5 flex flex-col gap-1.5"
                    style={{
                      border: "0.5px solid var(--hairline)",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary/30 shrink-0" />
                      <div className="h-1.5 w-1/2 rounded-sm bg-muted-foreground/20" />
                    </div>
                    <div className="flex gap-2 flex-1">
                      <div className="w-10 h-10 rounded-full border-[3px] border-primary/25 border-t-primary/50 shrink-0 self-center" />
                      <div className="flex flex-col justify-center gap-1 flex-1">
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full bg-primary/40 shrink-0" />
                          <div className="h-1 w-full rounded-sm bg-muted-foreground/15" />
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full bg-primary/30 shrink-0" />
                          <div className="h-1 w-3/4 rounded-sm bg-muted-foreground/12" />
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full bg-primary/25 shrink-0" />
                          <div className="h-1 w-2/3 rounded-sm bg-muted-foreground/10" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature highlights */}
              <div className="flex justify-center gap-8 mt-10">
                <div className="flex items-center gap-2">
                  <ChartBar size={18} className="text-foreground/70" />
                  <span className="text-sm text-foreground/70">
                    Auto-filled data
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <PaintBrush size={18} className="text-foreground/70" />
                  <span className="text-sm text-foreground/70">
                    White-labeled
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FilePdf size={18} className="text-foreground/70" />
                  <span className="text-sm text-foreground/70">
                    PDF & Slides
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation (§4.4) — proprietary modal, replaces window.confirm */}
      <DeleteReportDialog
        report={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o && !deleteMutation.isPending) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget);
        }}
        isPending={deleteMutation.isPending}
        error={
          deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : null
        }
      />

      {/* Submit-for-review confirmation (§4.4) — Story 24.2 */}
      <SubmitForReviewDialog
        report={submitTarget}
        open={!!submitTarget}
        onOpenChange={(o) => {
          if (!o && !submitMutation.isPending) setSubmitTarget(null);
        }}
        onConfirm={() => {
          if (submitTarget) submitMutation.mutate(submitTarget);
        }}
        isPending={submitMutation.isPending}
        error={
          submitMutation.error instanceof Error
            ? submitMutation.error.message
            : null
        }
      />

      {/* Assign reviewer (§4.4) — Story 24.3 (self-contained: own fetch + PATCH) */}
      <AssignReviewerDialog
        report={assignTarget}
        open={!!assignTarget}
        onOpenChange={(o) => {
          if (!o) setAssignTarget(null);
        }}
        onAssigned={() => showToast("success", "Reviewer updated")}
      />

      {/* Verdict actions (§4.4) — Story 24.4: approve / request changes / withdraw */}
      <ReviewActionDialog
        report={reviewTarget?.report ?? null}
        action={reviewTarget?.action ?? "approve"}
        open={!!reviewTarget}
        onOpenChange={(o) => {
          if (!o) setReviewTarget(null);
        }}
        onActioned={(action) => showToast("success", REVIEW_TOAST[action])}
      />

      {/* Approval history (§4.4 read-only timeline) — Story 24.6 */}
      <ApprovalHistoryDialog
        report={historyTarget}
        open={!!historyTarget}
        onOpenChange={(o) => {
          if (!o) setHistoryTarget(null);
        }}
      />

      <Toast toast={toast} />
    </div>
  );
}

// ============================================================================
// Delete confirmation dialog (§4.4 — centered modal, 2-button footer)
// ============================================================================

interface DeleteReportDialogProps {
  report: ReportListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
  error: string | null;
}

function DeleteReportDialog({
  report,
  open,
  onOpenChange,
  onConfirm,
  isPending,
  error,
}: DeleteReportDialogProps) {
  function handleOpenChange(next: boolean) {
    if (!next && isPending) return;
    onOpenChange(next);
  }
  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-[60] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="delete-report-dialog"
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delete report?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {report
                      ? `"${report.name}" will be permanently deleted. This action cannot be undone.`
                      : "This report will be permanently deleted. This action cannot be undone."}
                  </DialogPrimitive.Description>
                </div>
                {error && (
                  <p className="mt-4 text-[13px] text-destructive" role="alert">
                    {error}
                  </p>
                )}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => handleOpenChange(false)}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={onConfirm}
                    disabled={isPending}
                    data-testid="confirm-delete-report-btn"
                  >
                    <Trash size={16} className="mr-1" />
                    {isPending ? "Deleting…" : "Delete"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
